/** Named date periods (the DateRangePicker presets) and the date maths behind them. */

export const NAMED_DATE_PERIODS = [
  'today',
  'yesterday',
  'last7days',
  'last30days',
  'last90days',
  'last365days',
  'last_month',
  'last12months',
  'last13months',
  'last_year',
  'week_to_date',
  'month_to_date',
  'quarter_to_date',
  'year_to_date',
] as const;

export type NamedDatePeriod = (typeof NAMED_DATE_PERIODS)[number];

export function isNamedDatePeriod(period: string | null | undefined): period is NamedDatePeriod {
  return (NAMED_DATE_PERIODS as readonly string[]).includes(period ?? '');
}

export type DateRangeResolver = (period: string) => { start: Date; end: Date };

// Get date range for predefined periods
export const getDateRangeForPeriod = (
  period: string,
  today: Date = new Date()
): { start: Date; end: Date } => {
  const end = new Date(today);
  const start = new Date(today);

  switch (period) {
    case 'today':
      return { start, end };
    case 'yesterday':
      start.setDate(today.getDate() - 1);
      end.setDate(today.getDate() - 1);
      return { start, end };
    case 'last7days':
      start.setDate(today.getDate() - 6);
      return { start, end };
    case 'last30days':
      start.setDate(today.getDate() - 29);
      return { start, end };
    case 'last90days':
      start.setDate(today.getDate() - 89);
      return { start, end };
    case 'last365days':
      start.setDate(today.getDate() - 364);
      return { start, end };
    case 'last_month':
      start.setMonth(today.getMonth() - 1, 1);
      end.setMonth(today.getMonth(), 0);
      return { start, end };
    case 'last12months':
      start.setFullYear(today.getFullYear() - 1);
      return { start, end };
    case 'last13months': {
      // Full calendar months only: end = last day of previous month,
      // start = 1st of that month one year earlier (13 months inclusive).
      end.setMonth(today.getMonth(), 0);
      start.setFullYear(end.getFullYear() - 1, end.getMonth(), 1);
      return { start, end };
    }
    case 'last_year':
      start.setFullYear(today.getFullYear() - 1, 0, 1);
      end.setFullYear(today.getFullYear() - 1, 11, 31);
      return { start, end };
    case 'week_to_date':
      start.setDate(today.getDate() - today.getDay());
      return { start, end };
    case 'month_to_date':
      start.setDate(1);
      return { start, end };
    case 'quarter_to_date': {
      const quarter = Math.floor(today.getMonth() / 3);
      start.setMonth(quarter * 3, 1);
      return { start, end };
    }
    case 'year_to_date':
      start.setMonth(0, 1);
      return { start, end };
    default:
      return {
        start: new Date(2025, 1, 1),
        end: new Date(2025, 1, 28),
      };
  }
};

export const getComparisonDateRange = (
  start: Date,
  end: Date,
  comparisonType: string
): { start: Date; end: Date } | null => {
  if (comparisonType === 'none') {
    return null;
  }

  if (comparisonType === 'last_year') {
    const compStart = new Date(start);
    compStart.setFullYear(compStart.getFullYear() - 1);
    const compEnd = new Date(end);
    compEnd.setFullYear(compEnd.getFullYear() - 1);
    return { start: compStart, end: compEnd };
  }

  if (comparisonType === 'last_month') {
    if (start.getMonth() !== end.getMonth() || start.getFullYear() !== end.getFullYear()) {
      return null;
    }

    const compStart = new Date(start);
    compStart.setMonth(compStart.getMonth() - 1);

    const compEnd = new Date(end);
    compEnd.setMonth(compEnd.getMonth() - 1);

    if (compEnd.getDate() !== end.getDate()) {
      compEnd.setDate(0);
    }

    if (compStart.getDate() !== start.getDate()) {
      compStart.setDate(0);
    }

    return { start: compStart, end: compEnd };
  }

  console.warn(`Unknown comparison type: ${comparisonType}`);
  return null;
};

export const DATE_RANGE_OPTIONS = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 days', value: 'last7days' },
  { label: 'Last 30 days', value: 'last30days' },
  { label: 'Last 90 days', value: 'last90days' },
  { label: 'Last 365 days', value: 'last365days' },
  { label: 'Last month', value: 'last_month' },
  { label: 'Last 12 months', value: 'last12months' },
  { label: 'Last 13 months', value: 'last13months' },
  { label: 'Last year', value: 'last_year' },
  { label: 'Week to date', value: 'week_to_date' },
  { label: 'Month to date', value: 'month_to_date' },
  { label: 'Quarter to date', value: 'quarter_to_date' },
  { label: 'Year to date', value: 'year_to_date' },
];

export const COMPARISON_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: 'Previous year', value: 'last_year' },
  { label: 'Previous month', value: 'last_month' },
];

/** Same calendar days (times ignored). */
export function datesMatch(
  d1: { start: Date; end: Date },
  d2: { start: Date; end: Date }
): boolean {
  const start1 = new Date(d1.start.getFullYear(), d1.start.getMonth(), d1.start.getDate());
  const end1 = new Date(d1.end.getFullYear(), d1.end.getMonth(), d1.end.getDate());
  const start2 = new Date(d2.start.getFullYear(), d2.start.getMonth(), d2.start.getDate());
  const end2 = new Date(d2.end.getFullYear(), d2.end.getMonth(), d2.end.getDate());
  return start1.getTime() === start2.getTime() && end1.getTime() === end2.getTime();
}

/**
 * The period the picker shows for the dates it was given. A named period only stands when its
 * dates match today's; otherwise the dates are shown as Custom. With no period (or an unknown
 * one), the first preset with the same dates is picked.
 */
export function resolveInitialPeriod(
  start: Date | undefined,
  end: Date | undefined,
  period: string | undefined,
  resolve: DateRangeResolver = (p) => getDateRangeForPeriod(p)
): string {
  if (!start || !end) return period ?? 'last_month';
  if (period === 'custom') return 'custom';
  const dates = { start, end };
  if (isNamedDatePeriod(period)) {
    return datesMatch(dates, resolve(period)) ? period : 'custom';
  }
  return NAMED_DATE_PERIODS.find((p) => datesMatch(dates, resolve(p))) ?? 'custom';
}
